export type ResidentAddressKind = "home" | "mailing" | "property" | "project_site" | "other";

export type ResidentAddressSourceChannel =
  | "staff_manual"
  | "complaint"
  | "permit"
  | "backfill"
  | "import"
  | "email"
  | "connect_form"
  | "api";

export type ResidentSummary = {
  id: number;
  municipality_id: number;
  display_name: string;
  email: string | null;
  phone: string | null;
  last_interaction_at: string | null;
  open_complaint_count: number;
};

export type ResidentAddress = {
  id: number;
  kind: ResidentAddressKind;
  label: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  address_data: Record<string, unknown> | null;
  is_primary: boolean;
  source_channel: ResidentAddressSourceChannel;
  source_type: string | null;
  source_id: number | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type Resident = ResidentSummary & {
  first_name: string | null;
  last_name: string | null;
  internal_notes: string | null;
  metadata: Record<string, unknown>;
  addresses: ResidentAddress[];
  created_at: string;
  updated_at: string;
};

export type ResidentPayload = {
  display_name?: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  internal_notes?: string | null;
  metadata?: Record<string, unknown>;
  addresses?: Array<{
    kind?: ResidentAddressKind;
    label?: string | null;
    address_line1?: string;
    address_line2?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    is_primary?: boolean;
    address_data?: Record<string, unknown>;
  }>;
};

export type ResidentAddressPayload = {
  kind?: ResidentAddressKind;
  label?: string | null;
  address_line1?: string;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  is_primary?: boolean;
  address_data?: Record<string, unknown>;
};

export type ResidentsResponse = {
  residents: ResidentSummary[];
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  next_page: number | null;
};

export type ResidentTask = {
  id: number;
  resident_id: number;
  user_id: number;
  title: string;
  due_at: string | null;
  completed_at: string | null;
  position: number;
  created_at: string;
  updated_at: string;
};

export type ResidentEvent = {
  id: number;
  resident_id: number;
  user_id: number;
  event_type: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type ResidentTimelineEntry = {
  occurred_at: string;
  event_type: string;
  label: string;
  body: string | null;
  metadata: Record<string, unknown>;
  source: string;
};

export type ListResidentsParams = {
  page?: number;
  search?: string;
  signal?: AbortSignal;
};

const jsonHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

function residentsBasePath(municipalityId: number): string {
  return `/api/v1/municipalities/${municipalityId}/residents`;
}

export async function listResidents(
  municipalityId: number,
  params: ListResidentsParams = {},
): Promise<ResidentsResponse> {
  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set("page", String(params.page));
  if (params.search?.trim()) searchParams.set("search", params.search.trim());

  const query = searchParams.toString();
  const url = query ? `${residentsBasePath(municipalityId)}?${query}` : residentsBasePath(municipalityId);

  const response = await fetch(url, {
    credentials: "include",
    signal: params.signal,
  });

  if (!response.ok) {
    throw new Error("Failed to load residents");
  }

  return response.json() as Promise<ResidentsResponse>;
}

export async function getResident(
  municipalityId: number,
  residentId: number,
  signal?: AbortSignal,
): Promise<Resident> {
  const response = await fetch(`${residentsBasePath(municipalityId)}/${residentId}`, {
    credentials: "include",
    signal,
  });

  if (!response.ok) {
    throw new Error("Failed to load resident");
  }

  return response.json() as Promise<Resident>;
}

export async function createResident(
  municipalityId: number,
  payload: ResidentPayload,
): Promise<Resident> {
  const response = await fetch(residentsBasePath(municipalityId), {
    method: "POST",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ resident: payload }),
  });

  if (!response.ok) {
    throw new Error("Failed to create resident");
  }

  return response.json() as Promise<Resident>;
}

export async function updateResident(
  municipalityId: number,
  residentId: number,
  payload: ResidentPayload,
): Promise<Resident> {
  const response = await fetch(`${residentsBasePath(municipalityId)}/${residentId}`, {
    method: "PATCH",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ resident: payload }),
  });

  if (!response.ok) {
    throw new Error("Failed to update resident");
  }

  return response.json() as Promise<Resident>;
}

export async function listResidentAddresses(
  municipalityId: number,
  residentId: number,
): Promise<ResidentAddress[]> {
  const response = await fetch(`${residentsBasePath(municipalityId)}/${residentId}/addresses`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to load resident addresses");
  }

  return response.json() as Promise<ResidentAddress[]>;
}

export async function createResidentAddress(
  municipalityId: number,
  residentId: number,
  payload: ResidentAddressPayload,
): Promise<ResidentAddress> {
  const response = await fetch(`${residentsBasePath(municipalityId)}/${residentId}/addresses`, {
    method: "POST",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ resident_address: payload }),
  });

  if (!response.ok) {
    throw new Error("Failed to create resident address");
  }

  return response.json() as Promise<ResidentAddress>;
}

export async function updateResidentAddress(
  municipalityId: number,
  residentId: number,
  addressId: number,
  payload: ResidentAddressPayload,
): Promise<ResidentAddress> {
  const response = await fetch(
    `${residentsBasePath(municipalityId)}/${residentId}/addresses/${addressId}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({ resident_address: payload }),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to update resident address");
  }

  return response.json() as Promise<ResidentAddress>;
}

export async function deleteResidentAddress(
  municipalityId: number,
  residentId: number,
  addressId: number,
): Promise<void> {
  const response = await fetch(
    `${residentsBasePath(municipalityId)}/${residentId}/addresses/${addressId}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: { "X-CSRF-Token": csrfToken() },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to delete resident address");
  }
}

export async function listResidentTasks(
  municipalityId: number,
  residentId: number,
): Promise<ResidentTask[]> {
  const response = await fetch(`${residentsBasePath(municipalityId)}/${residentId}/tasks`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to load resident tasks");
  }

  return response.json() as Promise<ResidentTask[]>;
}

export async function createResidentTask(
  municipalityId: number,
  residentId: number,
  payload: { title: string; due_at?: string | null },
): Promise<ResidentTask> {
  const response = await fetch(`${residentsBasePath(municipalityId)}/${residentId}/tasks`, {
    method: "POST",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ resident_task: payload }),
  });

  if (!response.ok) {
    throw new Error("Failed to create resident task");
  }

  return response.json() as Promise<ResidentTask>;
}

export async function updateResidentTask(
  municipalityId: number,
  residentId: number,
  taskId: number,
  payload: { title?: string; due_at?: string | null; completed_at?: string | null },
): Promise<ResidentTask> {
  const response = await fetch(
    `${residentsBasePath(municipalityId)}/${residentId}/tasks/${taskId}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({ resident_task: payload }),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to update resident task");
  }

  return response.json() as Promise<ResidentTask>;
}

export async function deleteResidentTask(
  municipalityId: number,
  residentId: number,
  taskId: number,
): Promise<void> {
  const response = await fetch(
    `${residentsBasePath(municipalityId)}/${residentId}/tasks/${taskId}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: { "X-CSRF-Token": csrfToken() },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to delete resident task");
  }
}

export async function listResidentTimeline(
  municipalityId: number,
  residentId: number,
): Promise<ResidentTimelineEntry[]> {
  const response = await fetch(
    `${residentsBasePath(municipalityId)}/${residentId}/events/timeline`,
    { credentials: "include" },
  );

  if (!response.ok) {
    throw new Error("Failed to load resident timeline");
  }

  const payload = (await response.json()) as { timeline: ResidentTimelineEntry[] };
  return payload.timeline;
}

export async function listResidentComplaints(municipalityId: number, residentId: number) {
  const response = await fetch(`${residentsBasePath(municipalityId)}/${residentId}/complaints`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to load resident concerns");
  }

  return response.json();
}

export async function listResidentPermits(municipalityId: number, residentId: number) {
  const response = await fetch(`${residentsBasePath(municipalityId)}/${residentId}/permits`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to load resident permits");
  }

  return response.json();
}
